import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SelectSearchInput from "../SelectSearch";
import Select from 'react-select'
import axios from "axios";
import { formatSalesValue } from './../../utils/formatUtils';
import { toast } from "react-toastify";

export default function MenuEngieering() {
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const today = new Date().toISOString().split("T")[0];

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [menuData, setMenuData] = useState([]);
    const [categories, setCategories] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemType, setItemType] = useState("food");
    const [summary, setSummary] = useState({
        uniqueItems: 0,
        totalItems: 0,
        totalSold: 0,
        totalRevenue: 0,
        totalMenuCost: 0,
        totalContMargin: 0,
        avgContMargin: 0,
        potentialFoodCostPct: 0
    });

    const filteredMenuData = selectedItem
        ? menuData.filter(item => item.name === selectedItem)
        : menuData;

    useEffect(() => {
        if (selectedOutlet && token) {
            handleSearch();
        }
    }, [token, selectedOutlet]);

    const handleSearch = async () => {
        try {
            if (!startDate && !endDate) {
                toast.info('Please select a date range.')
            }

            const response = await axios.post(`${baseUrl}/menu-analytics`, {
                startDate: startDate,
                endDate: endDate,
                token: token,
                outlet: selectedOutlet,
                type: itemType
            });

            const data = response.data || [];

            const transformedData = data.itemwise_summary.map(item => ({
                name: item.ItemName,
                sold: item.total_sold,
                salesMix: item.sales_mix_percent.toFixed(2) + '%',
                salesPrice: parseFloat(item.sellingprice).toFixed(2),
                cost: parseFloat(item.costprice).toFixed(2),
                itemContMargin: parseFloat(item.itemContmargin).toFixed(2),
                revenue: parseFloat(item.Revenue).toFixed(2),
                menuCost: parseFloat(item.MenuCost).toFixed(2),
                menuContMargin: parseFloat(item.MenuContMargin).toFixed(2),
                salesMixCategory: item.salesMixCategory,
                contMarginCategory: item.ContMarginCategory,
                menuClassification: item.MenuClassification
            }));

            setMenuData(transformedData);
            setSelectedItem(null);
            setCategories(data.menu_classification_summary)

            setSummary({
                uniqueItems: data.unique_items_sold,
                totalItems: data.total_menu_items,
                totalSold: parseFloat(data.total_items_sold),
                totalRevenue: parseFloat(data.total_revenue),
                totalMenuCost: parseFloat(data.total_menu_cost),
                totalContMargin: parseFloat(data.total_menu_cont_margin),
                avgContMargin: parseFloat(data.avg_cont_margin),
                potentialFoodCostPct: (parseFloat(data.total_menu_cost) / parseFloat(data.total_revenue)) * 100
            });

        } catch (error) {
            console.error("Error fetching menu data:", error);
            toast.error("Error fetching menu data.")
        }
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
    };

    const handleTypeChange = (type) => {
        setItemType(type);
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="p-10 space-y-8">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <div className="flex flex-wrap gap-10 items-center">
                        <div className="w-80">
                            <Select
                                className="react-select-container w-full"
                                classNamePrefix="react-select"
                                options={menuData.map(item => ({
                                    value: item.name,
                                    label: item.name
                                }))}
                                isClearable
                                isSearchable
                                placeholder="Search item..."
                                onChange={(selectedOption) => setSelectedItem(selectedOption)}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '40px',
                                    }),
                                }}
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <span>From:</span>
                            <input
                                type="date"
                                className="border p-1 rounded"
                                value={startDate}
                                onChange={(e) => handleDateChange(e, 'start')}
                            />
                            <span>To:</span>
                            <input
                                type="date"
                                className="border p-1 rounded"
                                value={endDate}
                                onChange={(e) => handleDateChange(e, 'end')}
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1">
                                <input
                                    type="radio"
                                    name="type"
                                    checked={itemType === "food"}
                                    onChange={() => handleTypeChange("food")}
                                />
                                <span>Food</span>
                            </label>
                            <label className="flex items-center gap-1">
                                <input
                                    type="radio"
                                    name="type"
                                    checked={itemType === "beverage"}
                                    onChange={() => handleTypeChange("beverage")}
                                />
                                <span>Beverage</span>
                            </label>
                        </div>

                        <button
                            onClick={handleSearch}
                            className="border border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-4 py-2 text-sm"
                        >
                            Search
                        </button>
                    </div>

                    {menuData.length > 0 ? (
                        <>
                            <div className="rounded-md border overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            {[
                                                "Item Name",
                                                "Sold",
                                                "Sales Mix %",
                                                "Sales Price",
                                                "Cost",
                                                "Item Cont. Margin",
                                                "Revenue",
                                                "Menu Cost",
                                                "Menu Cont. Margin",
                                                "Sales Mix Category",
                                                "Cont. Margin Category",
                                                "Menu Classification",
                                            ].map((col, idx) => (
                                                <th
                                                    key={idx}
                                                    className="px-1 py-3 text-xs font-medium text-gray-700 uppercase tracking-wider"
                                                >
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {filteredMenuData.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-3 py-2">{item.name}</td>
                                                <td className="px-3 py-2">{item.sold}</td>
                                                <td className="px-3 py-2">{item.salesMix}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.salesPrice)}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.cost)}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.itemContMargin)}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.revenue)}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.menuCost)}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.menuContMargin)}</td>
                                                <td className="px-3 py-2">{item.salesMixCategory}</td>
                                                <td className="px-3 py-2">{item.contMarginCategory}</td>
                                                <td className="px-3 py-2">{item.menuClassification}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex justify-between text-sm text-gray-700 font-medium bg-gray-100 p-4 rounded-lg flex-wrap">
                                <div>Unique Items: <span className="text-black">{summary.uniqueItems.toLocaleString()}</span></div>
                                <div>Total Items: <span className="text-black">{summary.totalItems.toLocaleString()}</span></div>
                                <div>Total Sold: <span className="text-black">{summary.totalSold.toLocaleString()}</span></div>
                                <div>Total Revenue: <span className="text-black">{formatSalesValue(summary.totalRevenue)}</span></div>
                                <div>Total Menu Cost: <span className="text-black">{formatSalesValue(summary.totalMenuCost)}</span></div>
                                <div>Total Cont. Margin: <span className="text-black">{formatSalesValue(summary.totalContMargin)}</span></div>
                                <div>Avg. Cont. Margin: <span className="text-black">{formatSalesValue(summary.avgContMargin)}</span></div>
                                <div>Potential Food Cost %: <span className="text-black">{summary.potentialFoodCostPct.toFixed(2)}%</span></div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center mt-10 text-lg bg-gray-100 text-gray-700 font-medium p-4">
                            Menu data not available
                        </div>
                    )}

                    {Object.keys(categories).length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                            {["STAR", "PLOW HORSE", "PUZZLE", "DOG"].map((category) => {
                                const items = categories[category] || [];

                                return (
                                    <div key={category} className="border rounded-md">
                                        <div className="bg-gray-200 p-2 font-semibold text-center uppercase text-sm">
                                            {category}S
                                        </div>

                                        {items.length > 0 ? (
                                            <table className="min-w-full text-sm text-center divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-2 py-2">Item</th>
                                                        <th className="px-2 py-2">QTY</th>
                                                        <th className="px-2 py-2">Category</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {items.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td className="px-2 py-1">{item.ItemName}</td>
                                                            <td className="px-2 py-1">{item.total_sold}</td>
                                                            <td className="px-2 py-1">{category}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-gray-500 text-sm p-4 text-center">No items</div>
                                        )}
                                    </div>
                                );
                            })}

                        </div>
                    )}

                </div>
            </div>

            <Footer />
        </div>
    );
}